import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import { FieldColorPicker, FieldSegmented, FieldSlider, FormField } from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { DownloaderFormBrandType } from './downloaderTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.downloader.style, {
  downloader: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icon: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  label: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.downloader.schema.labelBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.downloader.schema.labelText'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  border: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useShowBorder, useBrandColorOptions],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.downloader.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const DownloaderFormStyle: DownloaderFormBrandType = ({
  device,
  subConfigs,
  element,
  edited,
  setForm,
}) => {
  const { font } = element || {}
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const values = useElementFontValues('downloader', device, font)
  const styles = useElementFontCss('downloader', device, font)

  const handleSetTypography = useCallback(() => setForm?.('base'), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.downloader.tags.downloader')}</Typography>
        <FormField config={schema.downloader.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.icon.color} />
        <FormField config={schema.label.backgroundColor} />
        <FormField config={schema.label.color} />

        <Typography weight='medium'>{t('elements.downloader.tags.border')}</Typography>
        <FormField config={schema.border.borderRadius} />
        <FormField config={schema.border.borderStyle} />
        <FormField config={schema.border.borderColor} />
        <FormField config={schema.border.borderWidth} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <BrandButtonFont
          edited={edited?.font?.base}
          font='base'
          onClick={handleSetTypography}
          styles={styles.base}
          values={values.base}
        >
          {t('page.brand.tab.typography')}
        </BrandButtonFont>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(DownloaderFormStyle) as typeof DownloaderFormStyle
